/**
 * Client-side crop export helpers for the evidence cropper.
 * Coordinate model: crop_box_percent is relative to the rotated display image
 * (rotate full source first, then crop — matches editor stage and backend apply-crop).
 */

/** Default export limits for Step 5 document attachments (readable, under upload cap). */
export const CROP_EXPORT_MAX_DIMENSION = 2200;
export const CROP_EXPORT_JPEG_QUALITY = 0.85;

/** Downscale a canvas so its longest side is at most maxDimension. */
export function scaleCanvasToMaxDimension(canvas, maxDimension = CROP_EXPORT_MAX_DIMENSION) {
  const maxSide = Math.max(canvas.width, canvas.height);
  if (!maxSide || maxSide <= maxDimension) return canvas;
  const scale = maxDimension / maxSide;
  const scaled = document.createElement('canvas');
  scaled.width = Math.max(1, Math.round(canvas.width * scale));
  scaled.height = Math.max(1, Math.round(canvas.height * scale));
  const ctx = scaled.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, scaled.width, scaled.height);
  ctx.drawImage(canvas, 0, 0, scaled.width, scaled.height);
  return scaled;
}

/** Compute axis-aligned bounding box after rotation. */
export function getRotatedCanvasDimensions(naturalWidth, naturalHeight, rotationDegrees) {
  if (!naturalWidth || !naturalHeight) {
    return { width: 0, height: 0 };
  }
  const rad = Math.abs(rotationDegrees) * (Math.PI / 180);
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  return {
    width: Math.round(naturalWidth * cos + naturalHeight * sin),
    height: Math.round(naturalWidth * sin + naturalHeight * cos),
  };
}

/** Map crop percentages to pixel rect on the rotated canvas. */
export function cropPixelsFromRotatedCanvas(cropArea, rotatedWidth, rotatedHeight) {
  let sourceX = Math.round((cropArea.x / 100) * rotatedWidth);
  let sourceY = Math.round((cropArea.y / 100) * rotatedHeight);
  let sourceWidth = Math.round((cropArea.width / 100) * rotatedWidth);
  let sourceHeight = Math.round((cropArea.height / 100) * rotatedHeight);

  sourceX = Math.max(0, Math.min(rotatedWidth - 1, sourceX));
  sourceY = Math.max(0, Math.min(rotatedHeight - 1, sourceY));
  if (sourceWidth < 1) sourceWidth = 1;
  if (sourceHeight < 1) sourceHeight = 1;
  if (sourceX + sourceWidth > rotatedWidth) sourceWidth = rotatedWidth - sourceX;
  if (sourceY + sourceHeight > rotatedHeight) sourceHeight = rotatedHeight - sourceY;

  return { sourceX, sourceY, sourceWidth, sourceHeight };
}

/**
 * Render the full source image onto a canvas with rotation applied.
 */
export function renderRotatedSourceCanvas(imageElement, totalRotation) {
  const naturalWidth = imageElement.naturalWidth;
  const naturalHeight = imageElement.naturalHeight;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (Math.abs(totalRotation) < 0.1) {
    canvas.width = naturalWidth;
    canvas.height = naturalHeight;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, naturalWidth, naturalHeight);
    ctx.drawImage(imageElement, 0, 0);
    return { canvas, width: naturalWidth, height: naturalHeight };
  }

  const { width, height } = getRotatedCanvasDimensions(naturalWidth, naturalHeight, totalRotation);
  canvas.width = width;
  canvas.height = height;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.save();
  ctx.translate(width / 2, height / 2);
  ctx.rotate((totalRotation * Math.PI) / 180);
  ctx.drawImage(imageElement, -naturalWidth / 2, -naturalHeight / 2, naturalWidth, naturalHeight);
  ctx.restore();

  return { canvas, width, height };
}

/**
 * Export a cropped region from an image element to a JPEG blob.
 * Applies rotation to the full source first, then crops — matching the editor overlay.
 */
export async function exportCroppedImageBlob(
  imageElement,
  cropArea,
  {
    rotation = 0,
    straighten = 0,
    quality = CROP_EXPORT_JPEG_QUALITY,
    maxOutputDimension = CROP_EXPORT_MAX_DIMENSION,
  } = {},
) {
  const naturalWidth = imageElement.naturalWidth;
  const naturalHeight = imageElement.naturalHeight;
  const totalRotation = rotation + straighten;

  const { canvas: rotatedCanvas, width: rotatedWidth, height: rotatedHeight } =
    renderRotatedSourceCanvas(imageElement, totalRotation);

  const { sourceX, sourceY, sourceWidth, sourceHeight } = cropPixelsFromRotatedCanvas(
    cropArea,
    rotatedWidth,
    rotatedHeight,
  );

  const canvas = document.createElement('canvas');
  canvas.width = sourceWidth;
  canvas.height = sourceHeight;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, sourceWidth, sourceHeight);
  ctx.drawImage(
    rotatedCanvas,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    sourceWidth,
    sourceHeight,
  );

  const outputCanvas = scaleCanvasToMaxDimension(canvas, maxOutputDimension);

  const blob = await new Promise((resolve) => {
    outputCanvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });

  return {
    blob,
    canvas: outputCanvas,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    naturalWidth,
    naturalHeight,
    rotatedWidth,
    rotatedHeight,
    rotationApplied: totalRotation,
  };
}

/**
 * Stack multiple cropped canvases vertically into one JPEG (multi-page PDF export).
 */
export async function combineCanvasesVertically(
  canvases,
  quality = CROP_EXPORT_JPEG_QUALITY,
  maxOutputDimension = CROP_EXPORT_MAX_DIMENSION,
) {
  if (!canvases.length) throw new Error('No canvases to combine');
  if (canvases.length === 1) {
    const scaled = scaleCanvasToMaxDimension(canvases[0], maxOutputDimension);
    const blob = await new Promise((resolve) => {
      scaled.toBlob((b) => resolve(b), 'image/jpeg', quality);
    });
    return { blob, canvas: scaled };
  }

  const maxWidth = Math.max(...canvases.map((c) => c.width));
  const totalHeight = canvases.reduce((sum, c) => sum + c.height, 0);
  const combined = document.createElement('canvas');
  combined.width = maxWidth;
  combined.height = totalHeight;
  const ctx = combined.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, maxWidth, totalHeight);

  let y = 0;
  for (const canvas of canvases) {
    const x = Math.floor((maxWidth - canvas.width) / 2);
    ctx.drawImage(canvas, x, y);
    y += canvas.height;
  }

  const scaledCombined = scaleCanvasToMaxDimension(combined, maxOutputDimension);

  const blob = await new Promise((resolve) => {
    scaledCombined.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });

  return { blob, canvas: scaledCombined };
}
